/**
 * useSidebarSearch — debounced backend search over chats + projects.
 *
 * 500ms debounce after the user stops typing. Short queries (< 2 chars)
 * clear results without hitting the backend. The service layer is the
 * only place that talks to `apiClient` — the component uses the hook.
 */

import { useCallback, useRef, useState } from 'react';
import {
    searchChats,
    searchChatsByUrl,
    type SearchResultChat,
} from '../../../modules/chat/chatMetadataService';
import { captureError } from '../../errors/captureError';

export type SearchResult = SearchResultChat;

type SearchPage = {
    results: SearchResult[];
    next: string | null;
};

export function useSidebarSearch() {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchOpen, setSearchOpen] = useState(false);
    const [apiSearchResults, setApiSearchResults] = useState<SearchPage | null>(null);
    const [isSearching, setIsSearching] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleSearchChange = useCallback((query: string) => {
        setSearchQuery(query);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!query.trim() || query.trim().length < 2) {
            setApiSearchResults(null);
            setIsSearching(false);
            return;
        }
        setIsSearching(true);
        debounceRef.current = setTimeout(async () => {
            try {
                const page = await searchChats(query);
                setApiSearchResults({ results: page.results, next: page.next });
            } catch (err) {
                captureError(err, { scope: 'useSidebarSearch', op: 'searchChats' });
                setApiSearchResults(null);
            }
            setIsSearching(false);
        }, 500);
    }, []);

    const loadMore = useCallback(async () => {
        if (!apiSearchResults?.next || isSearching) return;
        setIsSearching(true);
        try {
            const page = await searchChatsByUrl(apiSearchResults.next);
            setApiSearchResults((prev) => prev
                ? { results: [...prev.results, ...page.results], next: page.next }
                : { results: page.results, next: page.next });
        } catch (err) {
            /* non-blocking — leave already-loaded results */
            captureError(err, { scope: 'useSidebarSearch', op: 'loadMore' });
        }
        setIsSearching(false);
    }, [apiSearchResults, isSearching]);

    const close = useCallback(() => {
        setSearchOpen(false);
        setSearchQuery('');
        setApiSearchResults(null);
    }, []);

    return {
        searchQuery,
        setSearchQuery,
        searchOpen,
        setSearchOpen,
        apiSearchResults,
        isSearching,
        handleSearchChange,
        loadMore,
        close,
    };
}
